// MusicXML -> Score. The slice-5 import path: a score-partwise document maps
// concept-for-concept onto the score model — spelled pitch (step/alter/
// octave) is carried EXACTLY (never squeezed through MIDI), notated durations
// come from <type> + <dot> with <time-modification> runs becoming explicit
// TupletGroups, <chord/> tones join the previous event as extra noteheads,
// <tie> becomes per-notehead ties, and every <voice> is a first-class Voice.
// Multiple <part>s import as multiple Parts sharing the form.
//
// This is the app's MusicXML importer. Deliberate simplifications: tuplets
// are single-level (nested time-modifications flatten to the innermost
// ratio), <forward> without a voice is ignored, and unrecognised <type>
// values fall back to the divisions math for a straight value.

import { chordFromType, IREAL_QUALITY_BY_TYPE } from '../chart';
import { MUSICXML_CHORD_PARENTHESES_FIELD, parseChordParenthesesMetadata } from './chordParentheses';
import { parseBandCueMarker } from './bandCue';
import { parsePartPlaybackMarker } from './partPlayback';
import { mapQuality } from '../ireal/chordParser';
import { isNavigationDirective } from '../ireal/parse';
import type { BarlineClose, BarlineOpen } from '../ireal/types';
import { slotsToItems } from '../score/convert';
import { splitBassClefIntro, type StaffClefChange } from '../score/bassClefIntro';
import type { HarmonySymbol, KeySignature, Ornament, Spanner } from '../score/types';
import type {
  Clef,
  NoteEvent,
  NoteHead,
  Part,
  PartMeasure,
  PartRole,
  Score,
  StaffDef,
  TupletGroup,
  Voice,
  VoiceItem,
} from '../score/types';
import type { Duration, Fraction, NoteValue } from '../score/duration';
import { durationQuarters, equalsF, frac, fractionToNumber, NOTE_VALUES } from '../score/duration';
import type { DiatonicInterval, SpelledPitch, Step } from '../score/pitch';
import { invertInterval, midiOf, spellMidi, transposePitch } from '../score/pitch';
import { uid } from '../song';
import {
  applyChordDegrees,
  kindToChordType,
  MUSICXML_TRIAD_KINDS_FIELD,
  triadTypeFromKind,
  type ChordDegree,
} from './kind';
import {
  MUSICXML_FEEL_OTHER_PLAY_TYPE,
  MUSICXML_FORM_TEXT_OTHER_PLAY_TYPE,
  MUSICXML_PART_ROLE_FIELD,
  MUSICXML_PART_PLAY_ON_FIELD,
} from './feel';

function feelMarker(direction: Element): Element | undefined {
  return Array.from(direction.getElementsByTagName('other-play')).find(
    (node) => node.getAttribute('type') === MUSICXML_FEEL_OTHER_PLAY_TYPE,
  );
}

function formTextMarker(direction: Element): Element | undefined {
  return Array.from(direction.getElementsByTagName('other-play')).find(
    (node) => node.getAttribute('type') === MUSICXML_FORM_TEXT_OTHER_PLAY_TYPE,
  );
}

function isPartRole(value: string): value is PartRole {
  return value === 'lead' || value === 'comp' || value === 'bass' || value === 'other';
}

/**
 * A rehearsal/section mark carried by a direction: the dedicated <rehearsal>
 * element, or an enclosed <words> — Sibelius-style exports box their section
 * letters (and Chameleon its "intro"/"Coda") as
 * `<words enclosure="rectangle">` rather than using <rehearsal>. Both land on
 * `FormMeasure.section`, the same field iReal `*A` marks and openbook
 * `\myMark` reach, so the letters draw as rehearsal boxes and `analyzeForm`
 * can read named intros/codas.
 */
function sectionMark(direction: Element): string | null {
  const rehearsal = direction.getElementsByTagName('rehearsal')[0];
  if (rehearsal) return rehearsal.textContent?.trim() || null;
  for (const words of Array.from(direction.getElementsByTagName('words'))) {
    const enclosure = words.getAttribute('enclosure');
    if (enclosure && enclosure !== 'none') return words.textContent?.trim() || null;
  }
  return null;
}

const FIFTHS_MAJOR: Record<number, string> = {
  0: 'C',
  1: 'G',
  2: 'D',
  3: 'A',
  4: 'E',
  5: 'B',
  6: 'F#',
  7: 'C#',
  [-1]: 'F',
  [-2]: 'Bb',
  [-3]: 'Eb',
  [-4]: 'Ab',
  [-5]: 'Db',
  [-6]: 'Gb',
  [-7]: 'Cb',
};
const FIFTHS_MINOR: Record<number, string> = {
  0: 'A',
  1: 'E',
  2: 'B',
  3: 'F#',
  4: 'C#',
  5: 'G#',
  6: 'D#',
  7: 'A#',
  [-1]: 'D',
  [-2]: 'G',
  [-3]: 'C',
  [-4]: 'F',
  [-5]: 'Bb',
  [-6]: 'Eb',
  [-7]: 'Ab',
};

const text = (el: Element | null | undefined, tag: string): string | null =>
  el?.getElementsByTagName(tag)[0]?.textContent?.trim() ?? null;

const num = (el: Element | null | undefined, tag: string, fallback = 0): number => {
  const t = text(el, tag);
  const n = t === null ? NaN : Number(t);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Zero-based verse lane for a `<lyric number="…">`. The attribute is an
 * NMTOKEN, not an integer — Sibelius and Dorico write `part1verse1`, and
 * `Number()` on that is NaN, which as an array index is a string property:
 * the syllable vanished into `lyrics["NaN"]` while `lyrics.length` stayed 0,
 * so a whole corpus of exports imported and engraved with no words at all.
 * Take the LAST run of digits ("part1verse2" → verse 1); a token carrying no
 * digits sings in the first lane. Two parts' "verse1" both landing on lane 0
 * is correct — lyrics attach to events inside one part.
 */
export function verseIndex(raw: string | null | undefined): number {
  const digits = /(\d+)(?!.*\d)/.exec(raw ?? '');
  if (!digits) return 0;
  return Math.max(0, Number(digits[1]) - 1);
}

function clefOf(sign: string | null, line: number, octaveChange: number): Clef {
  if (sign === 'F') return 'bass';
  if (sign === 'C') return line === 4 ? 'tenor' : 'alto';
  if (sign === 'percussion') return 'percussion';
  return octaveChange < 0 ? 'treble8vb' : 'treble';
}

const NOTE_VALUE_SET = new Set<string>(NOTE_VALUES);

/** The written→sounding shift of a <transpose> element, octave-change folded
 *  in, or null when it is a no-op. MusicXML: sounding = written + chromatic. */
function transposeShiftOf(attributes: Element): DiatonicInterval | null {
  const el = attributes.getElementsByTagName('transpose')[0];
  if (!el) return null;
  const oct = num(el, 'octave-change', 0);
  const shift = {
    degrees: num(el, 'diatonic', 0) + 7 * oct,
    semitones: num(el, 'chromatic', 0) + 12 * oct,
  };
  return shift.degrees === 0 && shift.semitones === 0 ? null : shift;
}

/** Written pitch → concert. Falls back to a MIDI-true default spelling when
 *  the diatonic result would need a triple accidental. */
function toConcert(p: SpelledPitch, shift: DiatonicInterval): SpelledPitch {
  try {
    return transposePitch(p, shift);
  } catch {
    return spellMidi(midiOf(p) + shift.semitones);
  }
}

/** MusicXML <type> uses the same vocabulary as NoteValue. */
function noteValueOf(type: string | null): NoteValue | null {
  return type && NOTE_VALUE_SET.has(type) ? (type as NoteValue) : null;
}

/** MusicXML sound tempo counts quarters; a visual metronome may count any
 * note value. Resolve either to quarters before entering the score's clock. */
function quarterTempoOf(element: Element): number | undefined {
  for (const sound of Array.from(element.getElementsByTagName('sound'))) {
    const tempo = Number(sound.getAttribute('tempo'));
    if (Number.isFinite(tempo) && tempo > 0) return tempo;
  }
  for (const metronome of Array.from(element.getElementsByTagName('metronome'))) {
    const base = noteValueOf(text(metronome, 'beat-unit'));
    const bpm = num(metronome, 'per-minute', 0);
    if (!base || !Number.isFinite(bpm) || bpm <= 0) continue;
    const dots = Math.min(3, metronome.getElementsByTagName('beat-unit-dot').length) as 0 | 1 | 2 | 3;
    return bpm * fractionToNumber(durationQuarters({ base, dots }));
  }
  return undefined;
}

const ARTICULATION_TAGS: Record<string, 'staccato' | 'accent' | 'tenuto' | 'marcato'> = {
  staccato: 'staccato',
  accent: 'accent',
  tenuto: 'tenuto',
  'strong-accent': 'marcato',
  marcato: 'marcato', // Recover the nonstandard tag written by older app exports.
};

const ORNAMENT_TAGS: Record<string, Ornament> = {
  'trill-mark': 'trill',
  turn: 'turn',
  'inverted-turn': 'inverted-turn',
  mordent: 'mordent',
  'inverted-mordent': 'inverted-mordent',
};

/** Recognised ornaments on a note's <notations><ornaments> (wavy-line and
 *  tremolo are out of scope this slice). */
function ornamentsOf(node: Element): Ornament[] {
  const wrap = node.getElementsByTagName('ornaments')[0];
  if (!wrap) return [];
  const out: Ornament[] = [];
  for (const child of Array.from(wrap.children)) {
    const kind = ORNAMENT_TAGS[child.tagName];
    if (kind && !out.includes(kind)) out.push(kind);
  }
  return out;
}

/** Recognised articulations on a note's <notations><articulations>. */
function articulationsOf(node: Element): ('staccato' | 'accent' | 'tenuto' | 'marcato')[] {
  const wrap = node.getElementsByTagName('articulations')[0];
  if (!wrap) return [];
  const out: ('staccato' | 'accent' | 'tenuto' | 'marcato')[] = [];
  for (const child of Array.from(wrap.children)) {
    const kind = ARTICULATION_TAGS[child.tagName];
    if (kind && !out.includes(kind)) out.push(kind);
  }
  return out;
}

/** Straight-value fallback when <type> is missing: exact divisions match. */
function durationFromDivisions(duration: number, divisions: number): Duration {
  const quarters = frac(duration, divisions);
  for (const base of NOTE_VALUES) {
    for (const dots of [0, 1, 2] as const) {
      const d: Duration = dots ? { base, dots } : { base };
      if (equalsF(durationQuarters(d), quarters)) return d;
    }
  }
  return { base: 'quarter' };
}

/** Parse a MusicXML score-partwise string directly into the score model. */
export function parseMusicXMLScore(xml: string): Score {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('Invalid MusicXML: XML parse error');
  }
  const allPartEls = Array.from(doc.getElementsByTagName('part')).filter(
    (p) => p.getElementsByTagName('measure').length > 0,
  );
  // A part whose sounding content is entirely <unpitched> (a drum-kit part —
  // MuseScore exports of AI transcriptions list it FIRST) can say nothing the
  // model holds: every hit would import as a rest, drawing a staff of rest
  // noise, and its keyless signature would drive the form's key below. Skip
  // such parts while a pitched part exists; a drums-only file keeps its parts
  // so the import still yields a score rather than an error.
  const pitchedPartEls = allPartEls.filter(
    (p) =>
      p.getElementsByTagName('pitch').length > 0 ||
      p.getElementsByTagName('unpitched').length === 0,
  );
  const partEls = pitchedPartEls.length > 0 ? pitchedPartEls : allPartEls;
  if (partEls.length === 0) throw new Error('Invalid MusicXML: no <part> found');

  const title =
    text(doc.documentElement, 'movement-title') ??
    text(doc.getElementsByTagName('work')[0] as Element, 'work-title') ??
    'Imported score';
  const composer =
    Array.from(doc.getElementsByTagName('creator'))
      .find((c) => c.getAttribute('type') === 'composer')
      ?.textContent?.trim() || undefined;
  const identification = Array.from(doc.documentElement.children).find(
    (child) => child.tagName === 'identification',
  );
  // Our own exports carry this marker: their kinds and degrees spell the
  // exact catalogue type, triads included.
  const literalKinds = Array.from(identification?.getElementsByTagName('miscellaneous-field') ?? [])
    .some((field) =>
      field.getAttribute('name') === MUSICXML_TRIAD_KINDS_FIELD &&
      field.textContent?.trim() === 'literal',
    );
  const parentheses = parseChordParenthesesMetadata(
    Array.from(identification?.getElementsByTagName('miscellaneous-field') ?? [])
      .find((field) => field.getAttribute('name') === MUSICXML_CHORD_PARENTHESES_FIELD)?.textContent,
  );

  // Part names — and this app's own role marker — from the part-list,
  // matched by id. The role rides a miscellaneous-field because MusicXML has
  // no vocabulary for "this part is the melody"; an unrecognised value is
  // ignored rather than guessed at.
  const partNames = new Map<string, string>();
  const partRoles = new Map<string, PartRole>();
  const partPlayOn = new Map<string, 'all' | 'heads'>();
  for (const sp of Array.from(doc.getElementsByTagName('score-part'))) {
    const name = text(sp, 'part-name');
    const id = sp.getAttribute('id');
    if (id && name) partNames.set(id, name);
    const fields = Array.from(sp.getElementsByTagName('miscellaneous-field'));
    const role = fields
      .find((field) => field.getAttribute('name') === MUSICXML_PART_ROLE_FIELD)
      ?.textContent?.trim();
    if (id && role && isPartRole(role)) partRoles.set(id, role);
    const playOn = fields
      .find((field) => field.getAttribute('name') === MUSICXML_PART_PLAY_ON_FIELD)
      ?.textContent?.trim();
    if (id && (playOn === 'all' || playOn === 'heads')) partPlayOn.set(id, playOn);
  }

  // The FORM comes from the first kept part (every part aligns to it): meter,
  // key, tempo and the harmony lane. With drum-kit parts skipped above, that
  // is the first PITCHED part — a percussion part carries no key of its own,
  // so reading fifths=0 off a leading drum staff painted C major over music
  // in four sharps (found on a muscriptor transcription).
  const firstMeasures = Array.from(partEls[0].getElementsByTagName('measure'));
  const form = buildForm(firstMeasures, literalKinds, parentheses);

  const spanners: Spanner[] = [];
  const clefChanges: StaffClefChange[][] = [];
  const parts: Part[] = partEls.map((partEl, pi) => {
    const id = partEl.getAttribute('id') ?? `P${pi + 1}`;
    const part = buildPart(
      partEl,
      id,
      partNames.get(id) ?? `Part ${pi + 1}`,
      form.measures.length,
      spanners,
      (clefChanges[pi] = []),
    );
    const role = partRoles.get(id);
    const playOn = partPlayOn.get(id);
    return { ...part, ...(role ? { role } : {}), ...(playOn ? { playOn } : {}) };
  });

  // Tempo: the form scan covers the first part, but engravers often attach
  // the <sound tempo> direction to the accompaniment (Ave Maria puts "Sehr
  // langsam" on the piano) — fall back to the first one anywhere.
  let tempo = form.tempo;
  tempo ??= quarterTempoOf(doc.documentElement);
  // The app's tempo counts the opening meter's denominator (half notes in
  // 2/2, eighths in 6/8), while MusicXML <sound tempo> always counts quarters.
  if (tempo !== undefined) tempo = Math.round(tempo * (form.measures[0]?.timeSig?.[1] ?? 4) / 4);

  // Tempo/expression words ("Sehr langsam"): the first <direction><words>
  // that is actually a tempo DIRECTIVE — its direction carries <sound tempo>
  // or directive="yes". Plain words directions are other prose (the Telemann
  // writes instrument labels "Ob. Viol." / "Viola" as words) and must not be
  // promoted to a tempo marking.
  let tempoText: string | undefined;
  for (const words of Array.from(doc.getElementsByTagName('words'))) {
    const t = words.textContent?.trim();
    const dir = words.closest('direction');
    if (!t || !dir || formTextMarker(dir) || isNavigationDirective(t)) continue;
    const carriesFeel = !!feelMarker(dir);
    const hasTempoSound = Array.from(dir.getElementsByTagName('sound')).some(
      (sound) => sound.getAttribute('tempo') != null,
    );
    const isDirective = !carriesFeel && (dir.getAttribute('directive') === 'yes' || hasTempoSound);
    if (isDirective) {
      tempoText = t;
      break;
    }
  }

  const score: Score = {
    title,
    ...(composer ? { composer } : {}),
    ...(tempo !== undefined ? { tempo } : {}),
    ...(tempoText ? { tempoText } : {}),
    form: form.measures,
    parts,
    ...(spanners.length > 0 ? { spanners } : {}),
  };
  // A lead sheet that opens in the bass clef writes its intro for the piano:
  // the band plays it from a Piano part, the lead enters at the head. Any
  // other score comes back exactly as read.
  if (parts.length !== 1) return score;
  try {
    return splitBassClefIntro(score, clefChanges[0]);
  } catch {
    return score; // never let the optional split stop a tune from loading
  }
}

// ---------------------------------------------------------------------------
// Form (meter, key, tempo, harmony)
// ---------------------------------------------------------------------------

function buildForm(measures: Element[], literalKinds: boolean, parentheses: ReturnType<typeof parseChordParenthesesMetadata>): {
  measures: Score['form'];
  tempo?: number;
} {
  let divisions = 1;
  let timeSig: [number, number] = [4, 4];
  let tempo: number | undefined;
  // A transposing first part writes its key TRANSPOSED; the form stores the
  // concert key. An interval of (degrees D, semitones S) moves the key
  // signature by 7S − 12D fifths (M2 up = +2, m3 up = −3, P5 up = +1).
  let fifthsShift = 0;
  const out: Score['form'] = [];

  measures.forEach((measure, mi) => {
    const attributes = measure.getElementsByTagName('attributes')[0];
    let timeChanged = mi === 0;
    let keySig: KeySignature | undefined;
    if (attributes) {
      divisions = num(attributes, 'divisions', divisions) || divisions;
      const time = attributes.getElementsByTagName('time')[0];
      if (time) {
        // Additive meters ("3+2/8", "5+3+1/8") sum their groups — the
        // grouping is a display nicety; the TOTAL is the bar's length.
        const beatsText = text(time, 'beats');
        const beatsSum = beatsText
          ? beatsText.split('+').reduce((a, b) => a + (Number(b.trim()) || 0), 0)
          : 0;
        timeSig = [beatsSum > 0 ? beatsSum : timeSig[0], num(time, 'beat-type', timeSig[1])];
        timeChanged = true;
      }
      const shift = transposeShiftOf(attributes);
      if (shift) fifthsShift = 7 * shift.semitones - 12 * shift.degrees;
      const keyEl = attributes.getElementsByTagName('key')[0];
      if (keyEl && text(keyEl, 'fifths') !== null) {
        let fifths = num(keyEl, 'fifths', 0) + fifthsShift;
        while (fifths > 7) fifths -= 12; // enharmonic wrap into the table
        while (fifths < -7) fifths += 12;
        const mode = text(keyEl, 'mode') === 'minor' ? 'minor' : 'major';
        const tonic = (mode === 'minor' ? FIFTHS_MINOR : FIFTHS_MAJOR)[fifths];
        if (tonic) keySig = { tonic, mode };
      }
    }
    tempo ??= quarterTempoOf(measure);

    let feel: Score['form'][number]['feel'];
    let bandCue: Score['form'][number]['bandCue'];
    let section: string | undefined;
    let staffText: string | undefined;
    let staffTextAbove: boolean | undefined;
    for (const direction of Array.from(measure.getElementsByTagName('direction'))) {
      bandCue ??= parseBandCueMarker(direction);
      const formText = formTextMarker(direction)?.textContent?.trim();
      if (formText) {
        staffText = formText;
        staffTextAbove = direction.getAttribute('placement') === 'above';
      }
      if (!section) {
        const mark = sectionMark(direction);
        if (mark) section = mark;
      }
      if (feel) continue;
      const marker = feelMarker(direction);
      const label = marker?.textContent?.trim();
      if (!label) continue;
      const offsetUnits = num(direction, 'offset', 0);
      feel = {
        label,
        ...(offsetUnits ? { offset: frac(offsetUnits, divisions) } : {}),
      };
    }

    // Structure: <barline> repeats, voltas and double/final bar styles map
    // onto the form's own vocabulary (open/close/ending) — the same fields
    // flatten/unroll already expand for iReal charts. MusicXML puts a volta's
    // number on its START barline; the form marks the bar the bracket opens
    // on and derives its extent, so stop/discontinue carry no field. A
    // backward repeat's times= (beyond the default two passes) travels the
    // existing `Nx` staff-text channel that flatten's passesFor reads.
    let open: BarlineOpen | undefined;
    let close: BarlineClose | undefined;
    let ending: number | undefined;
    for (const bl of Array.from(measure.getElementsByTagName('barline'))) {
      const location = bl.getAttribute('location') ?? 'right';
      const repeat = bl.getElementsByTagName('repeat')[0];
      const endingEl = bl.getElementsByTagName('ending')[0];
      const style = text(bl, 'bar-style');
      if (location === 'left') {
        if (repeat?.getAttribute('direction') === 'forward') open = 'repeat';
        else if (style === 'light-light') open = 'double';
        if (endingEl?.getAttribute('type') === 'start') {
          const n = parseInt(endingEl.getAttribute('number') ?? '', 10);
          ending = Number.isFinite(n) ? n : 0;
        }
      } else {
        if (repeat?.getAttribute('direction') === 'backward') {
          close = 'repeat';
          const times = parseInt(repeat.getAttribute('times') ?? '', 10);
          if (Number.isFinite(times) && times > 2) staffText = `${times}x`;
        } else if (style === 'light-heavy' || style === 'heavy-light' || style === 'heavy-heavy') {
          close = 'final';
        } else if (style === 'light-light') {
          close = 'double';
        }
      }
    }

    // Harmony lane: chord symbols with quarter-length positions; durations run
    // to the next symbol or the barline, expressed in chart beats (denominator
    // units) like the rest of the app.
    const beatsPerQuarter = timeSig[1] / 4;
    const symbols: {
      atQ: number;
      root?: string;
      bass?: string;
      quality?: string;
      chordType?: ReturnType<typeof kindToChordType>;
      noChord?: boolean;
      isAlternate?: boolean;
      parentheses?: HarmonySymbol['parentheses'];
    }[] = [];
    let posQ = 0;
    let furthestQ = 0;
    // Earliest position at which any WRITTEN item (note or rest) begins —
    // the front of the bar is unwritten exactly when this exceeds zero.
    let firstWrittenQ = Infinity;
    let harmonyIndex = 0;
    for (const node of Array.from(measure.children)) {
      if (node.tagName === 'harmony') {
        const chordParentheses = parentheses.get(`${mi}:${harmonyIndex++}`);
        // MusicXML positions harmony at the current cursor plus an optional
        // signed offset, expressed in the measure's divisions. Exporters may
        // write a chord after the note it annotates and use a negative offset
        // to place it partway through that note.
        const atQ = posQ + num(node, 'offset', 0) / divisions;
        const rootEl = node.getElementsByTagName('root')[0];
        const step = text(rootEl, 'root-step');
        const kindEl = node.getElementsByTagName('kind')[0];
        const kind = kindEl?.textContent?.trim() || 'major';
        const functionText = text(node, 'function')?.replace(/\s/g, '').toUpperCase();
        const isNoChord =
          kind.toLowerCase() === 'none' || functionText === 'N.C.' || functionText === 'NC';
        // Guitar Pro emits a <harmony> for every chord DIAGRAM, including the
        // one-note diagrams it hangs off a single melody note. Those arrive as
        // <kind>other</kind> with no text attribute and no <degree> children —
        // no chord information whatsoever — and kindToChordType's dom7 default
        // would stamp a spurious dominant across the melody (8 phantom C7s over
        // the head of Lôro). A kind we cannot name, with nothing else to name it
        // by, is not a chord symbol; drop it rather than guess. This is distinct
        // from N.C., which is an authored instruction to play nothing.
        const unnameable =
          kind.toLowerCase() === 'other' &&
          kindEl?.getAttribute('text') == null &&
          node.getElementsByTagName('degree').length === 0;
        if (isNoChord) {
          symbols.push({ atQ, noChord: true });
        } else if (unnameable) {
          // no symbol — the bar keeps whatever harmony is already in force
        } else if (step) {
          const alter = num(rootEl, 'root-alter', 0);
          const acc = alter > 0 ? '#'.repeat(alter) : 'b'.repeat(-alter);
          const qualityAttribute = kindEl?.getAttribute('text');
          const quality = qualityAttribute == null ? undefined : qualityAttribute.trim();
          const degrees: ChordDegree[] = Array.from(node.getElementsByTagName('degree')).flatMap(
            (degree) => {
              const type = text(degree, 'degree-type');
              return type === 'add' || type === 'alter' || type === 'subtract'
                ? [
                    {
                      value: num(degree, 'degree-value', 0),
                      alter: num(degree, 'degree-alter', 0),
                      type,
                    },
                  ]
                : [];
            },
          );
          const chordType = applyChordDegrees(
            // A native export writes the exact catalogue type as kind plus
            // degrees and the text attribute as DISPLAY spelling only — the
            // book's "7alt" or "+" over a 7♯5, which the iReal quality reader
            // would take back as 7♭9 and ∆♯5. There the kind is the chord
            // (and a native triad stays a triad). A foreign file's text often
            // says more than its kind can ("9b5" over dominant-ninth), so it
            // keeps the quality reading.
            literalKinds
              ? (triadTypeFromKind(kind) ?? kindToChordType(kind))
              : quality
                ? mapQuality(quality)
                : kindToChordType(kind),
            degrees,
          );
          const bassEl = node.getElementsByTagName('bass')[0];
          const bassStep = text(bassEl, 'bass-step');
          const bassAlter = num(bassEl, 'bass-alter', 0);
          const bassAcc = bassAlter > 0 ? '#'.repeat(bassAlter) : 'b'.repeat(-bassAlter);
          symbols.push({
            atQ,
            ...(chordParentheses ? { parentheses: chordParentheses } : {}),
            ...(node.getAttribute('type') === 'alternate' ? { isAlternate: true } : {}),
            root: step.toUpperCase() + acc,
            ...(bassStep ? { bass: bassStep.toUpperCase() + bassAcc } : {}),
            ...(quality !== undefined ? { quality } : {}),
            chordType,
          });
        }
      } else if (node.tagName === 'note') {
        if (
          node.getElementsByTagName('chord').length === 0 &&
          node.getElementsByTagName('grace').length === 0
        ) {
          firstWrittenQ = Math.min(firstWrittenQ, posQ);
          posQ += num(node, 'duration', 0) / divisions;
          furthestQ = Math.max(furthestQ, posQ);
        }
      } else if (node.tagName === 'backup') {
        posQ -= num(node, 'duration', 0) / divisions;
      } else if (node.tagName === 'forward') {
        posQ += num(node, 'duration', 0) / divisions;
        furthestQ = Math.max(furthestQ, posQ);
      }
    }
    const fullBarQ = (timeSig[0] * 4) / timeSig[1];
    // An anacrusis arrives in THREE spellings: implicit="yes", a first
    // measure numbered 0 (the spec's own suggestion; 211 of the realbook
    // corpus), or — In a Sentimental Mood and ~120 others — a bare first
    // measure whose written time simply falls short of the meter, no marker
    // at all. The CONTENT is the test that covers all three: a composer who
    // meant silence writes rests (which advance the position walk), so a
    // first bar with real missing time can only be a pickup. A full bar that
    // merely got numbered 0 stays a full bar — shortening it would swallow
    // written beats. furthestQ spans every voice and staff (backup/forward
    // included), so a grand staff qualifies only when ALL its voices stop
    // short.
    // …and a FOURTH spelling: a leading <forward> then content to the bar
    // line — the gap is the un-played front of the bar (In a Sentimental
    // Mood, as actually exported). The discriminator is WHERE WRITTEN ITEMS
    // BEGIN: rests are written silence (a bar-rest measure starts at zero —
    // our own serializer parks harmony with forwards over one), while a true
    // anacrusis leaves the front of the bar with no item at all. The voice
    // walk drops voiceless forwards, so pickup content already starts at
    // the voice's own zero — exactly where a short bar's content belongs.
    const gapQ = Number.isFinite(firstWrittenQ) ? Math.max(0, firstWrittenQ) : 0;
    const soundingQ = furthestQ - gapQ;
    const isImplicitPickup =
      mi === 0 && soundingQ > 1e-9 && (furthestQ < fullBarQ - 1e-9 || gapQ > 1e-9);
    const barQ = isImplicitPickup ? soundingQ : fullBarQ;
    // Offsets can make document order differ from sounding order. Infer each
    // span only after ordering the symbols by their actual musical position.
    symbols.sort((a, b) => a.atQ - b.atQ);
    // An alternate is a choice at the same onset, not an extra sounding
    // chord that divides the primary's duration. Keep unpaired symbols so
    // external files with only an alternate do not lose their harmony.
    const primarySymbols = symbols.filter(
      (s) =>
        !s.isAlternate ||
        !symbols.some(
          (primary) => !primary.isAlternate && !primary.noChord && primary.atQ === s.atQ,
        ),
    );
    const harmony: HarmonySymbol[] = primarySymbols.map((s, i) => {
      const nextQ = i + 1 < primarySymbols.length ? primarySymbols[i + 1].atQ : barQ;
      const beats = Math.max(beatsPerQuarter / divisions, (nextQ - s.atQ) * beatsPerQuarter);
      if (s.noChord) {
        return {
          id: uid('c'),
          root: '',
          quality: '',
          chordType: null,
          beats,
          noChord: true,
          symbol: 'N.C.',
        };
      }
      const alternate = symbols.find(
        (candidate) => candidate !== s && candidate.isAlternate && candidate.atQ === s.atQ,
      );
      return {
        ...chordFromType(s.root!, s.chordType ?? 'maj7', beats, {
          bass: s.bass,
          quality: s.quality,
          parentheses: s.parentheses,
        }),
        // chordFromType deliberately protects general chart entry with a
        // one-beat floor. MusicXML can place harmony on subdivisions, so the
        // importer restores the exact inferred span after constructing it.
        beats,
        ...(alternate?.root && alternate.chordType
          ? {
              alternate: {
                root: alternate.root,
                chordType: alternate.chordType,
                quality: alternate.quality ?? IREAL_QUALITY_BY_TYPE[alternate.chordType],
                ...(alternate.bass ? { bass: alternate.bass } : {}),
                ...(alternate.parentheses ? { parentheses: alternate.parentheses } : {}),
              },
            }
          : {}),
      };
    });

    const words = Array.from(measure.getElementsByTagName('words'))
      .filter((node) => {
        const direction = node.closest('direction');
        return !direction || !formTextMarker(direction);
      })
      .map((node) => node.textContent?.trim() ?? '')
      .find(isNavigationDirective);
    const coda = measure.getElementsByTagName('coda').length > 0;
    const segno = measure.getElementsByTagName('segno').length > 0;
    const fermata = measure.getElementsByTagName('fermata').length > 0;

    out.push({
      id: uid('m'),
      harmony,
      ...(timeChanged ? { timeSig: [...timeSig] as [number, number] } : {}),
      ...(isImplicitPickup ? { pickupBeats: barQ * beatsPerQuarter } : {}),
      ...(keySig ? { keySig } : {}),
      ...(feel ? { feel } : {}),
      ...(bandCue ? { bandCue } : {}),
      ...(open ? { open } : {}),
      ...(close ? { close } : {}),
      ...(ending !== undefined ? { ending } : {}),
      ...(staffText ? { staffText } : {}),
      ...(staffTextAbove !== undefined ? { staffTextAbove } : {}),
      ...(coda ? { coda: true } : {}),
      ...(segno ? { segno: true } : {}),
      ...(fermata ? { fermata: true } : {}),
      ...(words ? { directive: words } : {}),
      ...(section ? { section } : {}),
    });
  });

  return { measures: out, tempo };
}

// ---------------------------------------------------------------------------
// Content (one Part)
// ---------------------------------------------------------------------------

function buildPart(
  partEl: Element,
  id: string,
  name: string,
  formLength: number,
  spanners: Spanner[],
  /** Collects every clef element WHERE it takes effect. The model keeps one
   * clef per staff (the last one read), so this is the only record of a
   * lead sheet's bass-clef intro (bassClefIntro.ts). */
  clefChanges: StaffClefChange[] = [],
): Part {
  let divisions = 1;
  // Open slurs keyed by VOICE + MusicXML slur number: a start on one event
  // pairs with the next stop of the same number in the same voice, routinely
  // crossing barlines (and systems — which is exactly why spanners reference
  // event ids, not positions). Voices interleave in document order via
  // <backup>, and exporters reuse number 1 across voices, so a part-wide
  // number key would let one voice's start clobber another's. Within a note,
  // stops are processed before starts: a chained slur writes stop+start on
  // the same note in either element order, and start-first would self-pair.
  const openSlurs = new Map<string, { from: string; side?: 'above' | 'below' }>();
  const collectSlurs = (node: Element, voiceKey: string, eventId: string) => {
    const slurs = Array.from(node.getElementsByTagName('slur'));
    for (const pass of ['stop', 'start'] as const) {
      for (const slur of slurs) {
        if (slur.getAttribute('type') !== pass) continue;
        const key = `${voiceKey}:${slur.getAttribute('number') ?? '1'}`;
        if (pass === 'stop') {
          const open = openSlurs.get(key);
          if (open) {
            spanners.push({
              id: uid('sp'),
              type: 'slur',
              fromEventId: open.from,
              toEventId: eventId,
              ...(open.side ? { side: open.side } : {}),
            });
            openSlurs.delete(key);
          }
        } else {
          const placement = slur.getAttribute('placement');
          openSlurs.set(key, {
            from: eventId,
            ...(placement === 'above' || placement === 'below' ? { side: placement } : {}),
          });
        }
      }
    }
  };
  // A <direction><dynamics> applies to the next note or rest (MusicXML attaches
  // dynamics to positions, our model to events). Keyed by voice when the
  // direction names one; '' = whichever voice's note comes next.
  const pendingDynamics = new Map<string, NonNullable<NoteEvent['dynamic']>>();
  const KNOWN_DYNAMICS = new Set(['pp', 'p', 'mp', 'mf', 'f', 'ff']);
  const dynamicOf = (node: Element): NoteEvent['dynamic'] => {
    const dynamics = node.getElementsByTagName('dynamics')[0];
    return Array.from(dynamics?.children ?? []).find((child) => KNOWN_DYNAMICS.has(child.tagName))
      ?.tagName as NoteEvent['dynamic'];
  };
  const takeDynamic = (voiceKey: string): NoteEvent['dynamic'] => {
    const hit = pendingDynamics.get(voiceKey) ?? pendingDynamics.get('');
    if (hit !== undefined) {
      pendingDynamics.delete(voiceKey);
      pendingDynamics.delete('');
    }
    return hit;
  };
  // Plain <direction><words> is staff text — instrument cues ("Ob. Viol."),
  // expression prose — attached to the next event ON ITS STAFF (the Telemann
  // keys its cues by <staff>, and "Bc." must not print over the treble). The
  // placement side travels too: above-cues ride over the staff, below-cues
  // (the Telemann's "Viola") under it. Tempo DIRECTIVE words (<sound tempo> /
  // directive="yes") are the score-level tempoText and never double as
  // staff text.
  const pendingWords = new Map<string, string>();
  const takeWords = (staffKey: string, side: 'above' | 'below'): string | undefined => {
    const key = `${staffKey}|${side}`;
    const hit = pendingWords.get(key);
    if (hit !== undefined) pendingWords.delete(key);
    return hit;
  };
  // Hairpins: a <wedge type="crescendo|diminuendo"> direction opens a wedge
  // whose fromEventId is the NEXT note event; type="stop" closes it on the
  // most recent note. Keyed by the wedge's number attribute (default 1).
  const openWedges = new Map<
    string,
    { type: 'cresc' | 'dim'; fromId: string | null; side?: 'above' | 'below' }
  >();
  let lastNoteEventId: string | null = null;
  // The last non-chord NOTE event in DOCUMENT order — where a <chord/> member
  // without its own <voice> tag belongs (Sibelius omits it; see below).
  let lastDocNote: { event: NoteEvent; laneKey: string } | null = null;
  const noteEventSeen = (id: string) => {
    lastNoteEventId = id;
    for (const w of openWedges.values()) if (w.fromId === null) w.fromId = id;
  };
  // Transposing instrument: <transpose> gives sounding = written + shift.
  // The model stores CONCERT pitch as truth, so every written pitch converts
  // on the way in, and Part.transposition remembers the written view
  // (written = concert + transposition).
  let soundShift: DiatonicInterval | null = null;
  const staves: StaffDef[] = [];
  const ensureStaff = (index: number) => {
    while (staves.length <= index) {
      staves.push({ id: `staff:${id}:${staves.length}`, clef: 'treble', staffType: 'standard' });
    }
  };
  ensureStaff(0);

  const measures: PartMeasure[] = Array.from(partEl.getElementsByTagName('measure')).map(
    (measure, measureIndex) => {
      const attributes = measure.getElementsByTagName('attributes')[0];
      if (attributes) {
        divisions = num(attributes, 'divisions', divisions) || divisions;
        if (!soundShift) soundShift = transposeShiftOf(attributes);
        ensureStaff(num(attributes, 'staves', staves.length) - 1);
        for (const clefEl of Array.from(attributes.getElementsByTagName('clef'))) {
          const staffIndex = Number(clefEl.getAttribute('number') ?? '1') - 1;
          ensureStaff(staffIndex);
          staves[staffIndex] = {
            ...staves[staffIndex],
            clef: clefOf(
              text(clefEl, 'sign'),
              num(clefEl, 'line', 3),
              num(clefEl, 'clef-octave-change', 0),
            ),
          };
        }
      }

      // MusicXML's cursor is measure-wide, not voice-wide. Most writers use a
      // distinct <voice> number after <backup>, but piano exports may reuse a
      // number on both staves. Keep cursor-aware lanes for each XML voice: a
      // rewind into occupied time starts a parallel model Voice, while a
      // forward jump inserts exact silence before continuing the same lane.
      interface VoiceBuild {
        key: string;
        xmlVoice: string;
        lane: number;
        items: VoiceItem[];
        tuplet: TupletGroup | null;
        lastEvent: NoteEvent | null;
        staffVotes: Map<number, number>;
        positionDiv: number;
        lastStaff?: number;
      }
      const measureChildren = Array.from(measure.children);
      let scanCursorDiv = 0;
      let contentOriginDiv = Number.POSITIVE_INFINITY;
      for (const node of measureChildren) {
        if (node.tagName === 'backup') {
          scanCursorDiv = Math.max(0, scanCursorDiv - num(node, 'duration', 0));
        } else if (node.tagName === 'forward') {
          if (text(node, 'voice')) contentOriginDiv = Math.min(contentOriginDiv, scanCursorDiv);
          scanCursorDiv += num(node, 'duration', 0);
        } else if (
          node.tagName === 'note' &&
          node.getElementsByTagName('chord').length === 0
        ) {
          contentOriginDiv = Math.min(contentOriginDiv, scanCursorDiv);
          if (node.getElementsByTagName('grace').length === 0) {
            scanCursorDiv += num(node, 'duration', 0);
          }
        }
      }
      if (!Number.isFinite(contentOriginDiv)) contentOriginDiv = 0;

      const voices: VoiceBuild[] = [];
      const lanesByVoice = new Map<string, VoiceBuild[]>();
      const noteByXmlVoice = new Map<string, { event: NoteEvent; laneKey: string }>();
      let cursorDiv = 0;

      const newVoice = (xmlVoice: string): VoiceBuild => {
        const lanes = lanesByVoice.get(xmlVoice) ?? [];
        const lane = lanes.length;
        const v: VoiceBuild = {
          key: lane === 0 ? xmlVoice : `${xmlVoice}#${lane + 1}`,
          xmlVoice,
          lane,
          items: [],
          tuplet: null,
          lastEvent: null,
          staffVotes: new Map(),
          positionDiv: contentOriginDiv,
        };
        lanes.push(v);
        lanesByVoice.set(xmlVoice, lanes);
        voices.push(v);
        return v;
      };

      const appendGap = (v: VoiceBuild, gapDiv: number): void => {
        if (gapDiv <= 0) return;
        closeTuplet(v);
        try {
          v.items.push(
            ...slotsToItems([
              {
                quarters: frac(gapDiv, divisions),
                idBase: uid('ev'),
              },
            ]),
          );
        } catch {
          // Exotic division grids can describe gaps smaller than the model's
          // 128th-note floor. Preserve importability just as direct notes do;
          // the cursor still keeps later lane selection faithful to the XML.
          v.items.push({
            id: uid('ev'),
            kind: 'rest',
            duration: durationFromDivisions(gapDiv, divisions),
            notes: [],
          });
        }
        v.positionDiv += gapDiv;
        v.lastEvent = null;
      };

      const voiceAt = (xmlVoice: string, atDiv: number, staffIndex: number): VoiceBuild => {
        const lanes = lanesByVoice.get(xmlVoice) ?? [];
        const eligible = lanes.filter((lane) => lane.positionDiv <= atDiv);
        let v: VoiceBuild | undefined;
        if (eligible.length > 0) {
          const latest = Math.max(...eligible.map((lane) => lane.positionDiv));
          const closest = eligible.filter((lane) => lane.positionDiv === latest);
          v = closest.find((lane) => lane.lastStaff === staffIndex) ?? closest[0];
        }
        if (!v) v = newVoice(xmlVoice);
        appendGap(v, atDiv - v.positionDiv);
        return v;
      };

      for (const node of measureChildren) {
        if (node.tagName === 'attributes') {
          // A clef can change between notes (Aisha's turns treble on beat 4
          // for the head's pickup). Voice time starts at the content origin.
          const offset = frac(Math.max(0, cursorDiv - contentOriginDiv), divisions);
          for (const clefEl of Array.from(node.getElementsByTagName('clef'))) {
            clefChanges.push({
              staff: Number(clefEl.getAttribute('number') ?? '1') - 1,
              measureIndex,
              offset,
              clef: clefOf(
                text(clefEl, 'sign'),
                num(clefEl, 'line', 3),
                num(clefEl, 'clef-octave-change', 0),
              ),
            });
          }
          continue;
        }
        if (node.tagName === 'direction') {
          const dynamic = dynamicOf(node);
          if (dynamic) pendingDynamics.set(text(node, 'voice') ?? '', dynamic);
          const wordsEl = node.getElementsByTagName('words')[0];
          const wordsText = wordsEl?.textContent?.trim();
          const isTempoDirective =
            !!feelMarker(node) ||
            node.getAttribute('directive') === 'yes' ||
            node.getElementsByTagName('sound')[0]?.getAttribute('tempo') != null;
          // A section mark (boxed words / <rehearsal>) is form structure, not
          // staff text — buildForm owns it, and attaching it here too would
          // print "intro" twice, once boxed and once italic on the note.
          if (
            wordsText &&
            !isTempoDirective &&
            !formTextMarker(node) &&
            !isNavigationDirective(wordsText) &&
            sectionMark(node) === null
          ) {
            const side = node.getAttribute('placement') === 'below' ? 'below' : 'above';
            const key = `${text(node, 'staff') ?? '1'}|${side}`;
            const prev = pendingWords.get(key);
            // Consecutive same-side cues before one note stack ("Ob. Viol." + "Ob.").
            pendingWords.set(key, prev ? `${prev} ${wordsText}` : wordsText);
          }
          for (const wedge of Array.from(node.getElementsByTagName('wedge'))) {
            const wKey = wedge.getAttribute('number') ?? '1';
            const wType = wedge.getAttribute('type');
            if (wType === 'crescendo' || wType === 'diminuendo') {
              const placement = node.getAttribute('placement');
              openWedges.set(wKey, {
                type: wType === 'crescendo' ? 'cresc' : 'dim',
                fromId: null,
                ...(placement === 'above' || placement === 'below' ? { side: placement } : {}),
              });
            } else if (wType === 'stop') {
              const open = openWedges.get(wKey);
              if (open) {
                openWedges.delete(wKey);
                const fromId = open.fromId;
                const toId = lastNoteEventId ?? fromId;
                if (fromId && toId) {
                  spanners.push({
                    id: uid('sp'),
                    type: open.type,
                    fromEventId: fromId,
                    toEventId: toId,
                    ...(open.side ? { side: open.side } : {}),
                  });
                }
              }
            }
          }
          continue;
        }
        if (node.tagName === 'backup') {
          cursorDiv = Math.max(0, cursorDiv - num(node, 'duration', 0));
          lastDocNote = null;
          continue;
        }
        if (node.tagName === 'forward') {
          const forwardDiv = num(node, 'duration', 0);
          const voiceKey = text(node, 'voice');
          if (voiceKey) {
            const staffIndex = Number(text(node, 'staff') ?? '1') - 1;
            ensureStaff(staffIndex);
            const v = voiceAt(voiceKey, cursorDiv, staffIndex);
            appendGap(v, forwardDiv);
            v.staffVotes.set(staffIndex, (v.staffVotes.get(staffIndex) ?? 0) + 1);
            v.lastStaff = staffIndex;
          }
          cursorDiv += forwardDiv;
          lastDocNote = null;
          continue;
        }
        if (node.tagName !== 'note') continue;

        const explicitVoice = text(node, 'voice');
        const isChordTone = node.getElementsByTagName('chord').length > 0;
        // A <chord/> member joins the note it follows IN DOCUMENT ORDER.
        // Sibelius (the realbook corpus) omits <voice> on chord members, and
        // the old `?? '1'` default glued every voice-2 dyad head onto voice
        // 1's latest note — one voice grew five-head clusters while the other
        // rendered bare bottoms (All Blues). An explicit voice still targets
        // that voice's own last event, as before.
        if (isChordTone) {
          const previous = explicitVoice ? noteByXmlVoice.get(explicitVoice) : lastDocNote;
          const target = previous?.event;
          if (target && target.kind === 'note') {
            const head = noteheadOf(node);
            if (head) target.notes.push(head);
            const dynamic = dynamicOf(node);
            if (dynamic) target.dynamic = dynamic;
            collectSlurs(node, previous?.laneKey ?? explicitVoice ?? '1', target.id);
          }
          continue;
        }

        const voiceKey = explicitVoice ?? '1';
        const staffIndex = Number(text(node, 'staff') ?? '1') - 1;
        ensureStaff(staffIndex);
        const grace = node.getElementsByTagName('grace').length > 0;
        const soundingDiv = grace ? 0 : num(node, 'duration', 0);
        const v = voiceAt(voiceKey, cursorDiv, staffIndex);
        cursorDiv += soundingDiv;
        v.staffVotes.set(staffIndex, (v.staffVotes.get(staffIndex) ?? 0) + 1);
        v.lastStaff = staffIndex;

        const cue =
          node.getElementsByTagName('cue').length > 0 ||
          node.getElementsByTagName('type')[0]?.getAttribute('size') === 'cue';
        const isRest = node.getElementsByTagName('rest').length > 0;
        const slash = !isRest && text(node, 'notehead') === 'slash' &&
          node.getElementsByTagName('unpitched').length > 0
          ? (text(node, 'stem') === 'none' ? 'beat' : 'rhythm') : undefined;
        let duration =
          noteValueDuration(node) ??
          (grace
            ? { base: 'eighth' as const }
            : durationFromDivisions(num(node, 'duration', 0), divisions));
        // Whole-bar rest convention: engravers write a bar rest as a WHOLE
        // rest (or measure="yes") in any meter — Finale emits <type>whole</
        // type> with <duration> covering a 3/8 bar. For a plain (untuplet)
        // rest, the sounding <duration> is the truth; the notated type is
        // just the bar-rest sign. Tuplet rests keep their written type (their
        // raw duration is scaled by the ratio, so it would mismatch).
        // A bar no single value holds (five quarters in 5/4, 5/8, 9/8)
        // is written as the rests that do, like any other gap:
        // durationFromDivisions falls back to ONE QUARTER there, which left
        // a MuseScore bar rest — and our own export of an empty measure —
        // four beats short.
        let restSpan: Fraction | null = null;
        if (isRest && !grace && node.getElementsByTagName('time-modification').length === 0) {
          const divs = num(node, 'duration', 0);
          const sounding = frac(divs, divisions);
          if (divs > 0 && !equalsF(durationQuarters(duration), sounding)) {
            duration = durationFromDivisions(divs, divisions);
            if (!equalsF(durationQuarters(duration), sounding)) restSpan = sounding;
          }
        }
        const event: NoteEvent = {
          id: uid('ev'),
          kind: isRest ? 'rest' : 'note',
          duration,
          notes: [],
          ...(slash ? { slash } : {}),
          staff: staffIndex,
          ...(grace ? { grace: true } : {}),
          ...(cue && !grace ? { cue: true } : {}),
        };
        // Dynamics are legal both as a direction and inside a note's
        // <notations> (the form our exporter writes). Consume an earlier
        // direction even when a local mark takes precedence, so it cannot
        // leak onto the following event. Rests can carry a dynamic change too.
        const pendingDynamic = takeDynamic(voiceKey);
        const dynamic = dynamicOf(node) ?? pendingDynamic;
        if (dynamic) event.dynamic = dynamic;
        // Tacet and entrance cues often sit on rests. Waiting for a pitched
        // note would move them into a later bar, or lose a final tacet.
        const noteStaff = text(node, 'staff') ?? '1';
        const words = takeWords(noteStaff, 'above');
        if (words) event.words = words;
        const wordsBelow = takeWords(noteStaff, 'below');
        if (wordsBelow) event.wordsBelow = wordsBelow;
        if (!isRest) {
          const artics = articulationsOf(node);
          if (artics.length > 0) event.articulations = artics;
          const orns = ornamentsOf(node);
          if (orns.length > 0) event.ornaments = orns;
          // Sung verses: every <lyric> child, keyed by its number (verse).
          for (const ly of Array.from(node.getElementsByTagName('lyric'))) {
            const t = text(ly, 'text');
            if (!t) continue;
            const verse = verseIndex(ly.getAttribute('number'));
            const syllabic = text(ly, 'syllabic');
            const lyrics = event.lyrics ?? (event.lyrics = []);
            lyrics[verse] = {
              text: t,
              ...(syllabic === 'begin' || syllabic === 'middle' ? { hyphen: true } : {}),
            };
          }
        }
        if (!isRest && !slash) {
          const head = noteheadOf(node);
          if (!head) {
            appendGap(v, soundingDiv);
            continue;
          }
          event.notes.push(head);
        }
        collectSlurs(node, v.key, event.id);
        if (!isRest) noteEventSeen(event.id);

        // Tuplet membership from <time-modification>; runs of the same ratio
        // share one group, closed by a ratio change or a plain note.
        const tm = node.getElementsByTagName('time-modification')[0];
        // A grace takes no time, so it never ends a running tuplet: one
        // written inside the bracket (Spain's r–(g)E–D half-note triplet,
        // Cow-Cow Boogie's D–B–(g)C) joins the group beside its principal.
        // Closing the group there split the triplet in two, and the bracket
        // and figure covered only part of it.
        if (grace && v.tuplet) {
          v.tuplet.items.push(event);
          v.lastEvent = event;
          noteByXmlVoice.set(voiceKey, { event, laneKey: v.key });
          if (event.kind === 'note') lastDocNote = { event, laneKey: v.key };
          continue;
        }
        if (tm && !grace) {
          const actual = num(tm, 'actual-notes', 1);
          const normal = num(tm, 'normal-notes', 1);
          if (actual > 1 && normal >= 1 && actual !== normal) {
            if (v.tuplet && (v.tuplet.actual !== actual || v.tuplet.normal !== normal)) {
              closeTuplet(v);
            }
            if (!v.tuplet) {
              v.tuplet = { id: uid('tup'), kind: 'tuplet', actual, normal, items: [] };
            }
            v.tuplet.items.push(event);
            v.lastEvent = event;
            v.positionDiv += soundingDiv;
            noteByXmlVoice.set(voiceKey, { event, laneKey: v.key });
            if (event.kind === 'note') lastDocNote = { event, laneKey: v.key };
            const stops = Array.from(node.getElementsByTagName('tuplet')).some(
              (t) => t.getAttribute('type') === 'stop',
            );
            if (stops) closeTuplet(v);
            continue;
          }
        }
        closeTuplet(v);
        v.items.push(...(restSpan ? restPieces(event, restSpan) : [event]));
        v.lastEvent = event;
        v.positionDiv += soundingDiv;
        noteByXmlVoice.set(voiceKey, { event, laneKey: v.key });
        if (event.kind === 'note') lastDocNote = { event, laneKey: v.key };
      }

      const built: Voice[] = voices
        .sort(
          (a, b) =>
            a.xmlVoice.localeCompare(b.xmlVoice, undefined, { numeric: true }) || a.lane - b.lane,
        )
        .map((v) => {
          closeTuplet(v);
          const staff = [...v.staffVotes.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 0;
          const retainCrossStaff = (items: VoiceItem[]): void => {
            for (const item of items) {
              if (item.kind === 'tuplet') retainCrossStaff(item.items);
              else if (item.staff === staff) delete item.staff;
            }
          };
          retainCrossStaff(v.items);
          return {
            id: uid('v'),
            ...(staff > 0 ? { staff } : {}),
            items: v.items,
          };
        })
        .filter((v) => v.items.length > 0);

      const playback = parsePartPlaybackMarker(measure);
      return { id: uid('pm'), voices: built, ...(playback?.length ? { playback } : {}) };

      // The pieces keep the written rest's id and marks on the first.
      function restPieces(rest: NoteEvent, span: Fraction): VoiceItem[] {
        try {
          return slotsToItems([{ quarters: span, idBase: rest.id, source: rest }]);
        } catch {
          return [rest]; // below the model's 128th floor: keep the rest as read
        }
      }

      function closeTuplet(v: VoiceBuild): void {
        if (!v.tuplet) return;
        // A group ends on a sounding member. Graces trailing a run with no
        // stop mark belong to the note after it (or to none, at a barline),
        // so they follow the group rather than sit inside its bracket.
        const items = v.tuplet.items;
        const isGrace = (item: VoiceItem) => item.kind !== 'tuplet' && item.grace === true;
        let end = items.length;
        while (end > 0 && isGrace(items[end - 1])) end--;
        const trailing = items.splice(end);
        v.items.push(v.tuplet, ...trailing);
        v.tuplet = null;
      }
    },
  );

  // Align to the form (padded/truncated again by normalizeScore at commit).
  while (measures.length < formLength) measures.push({ id: uid('pm'), voices: [] });

  // Convert written pitches to concert truth once the whole part is built
  // (tuplet members included — they live inside TupletGroup items).
  if (soundShift) {
    const shift = soundShift;
    const transposeItems = (items: VoiceItem[]): void => {
      for (const item of items) {
        if (item.kind === 'tuplet')
          transposeItems(item.items); // tuplets nest
        else for (const head of item.notes) head.pitch = toConcert(head.pitch, shift);
      }
    };
    for (const pm of measures) for (const voice of pm.voices) transposeItems(voice.items);
  }

  return {
    id: `part:${id}`,
    name,
    staves,
    measures: measures.slice(0, formLength),
    ...(soundShift ? { transposition: invertInterval(soundShift) } : {}),
  };
}

function noteValueDuration(node: Element): Duration | null {
  const base = noteValueOf(text(node, 'type'));
  if (!base) return null;
  const dots = Math.min(3, node.getElementsByTagName('dot').length) as 0 | 1 | 2 | 3;
  return dots ? { base, dots } : { base };
}

function noteheadOf(node: Element): NoteHead | null {
  const pitchEl = node.getElementsByTagName('pitch')[0];
  const stepText = text(pitchEl, 'step')?.toUpperCase();
  if (!pitchEl || !stepText || !'ABCDEFG'.includes(stepText)) return null;
  const pitch: SpelledPitch = {
    step: stepText as Step,
    alter: Math.max(-2, Math.min(2, num(pitchEl, 'alter', 0))),
    octave: num(pitchEl, 'octave', 4),
  };
  const ties = Array.from(node.getElementsByTagName('tie')).map((t) => t.getAttribute('type'));
  const start = ties.includes('start');
  const stop = ties.includes('stop');
  const tie =
    start && stop
      ? ('both' as const)
      : start
        ? ('start' as const)
        : stop
          ? ('stop' as const)
          : undefined;
  return { pitch, ...(tie ? { tie } : {}) };
}
